// 261012 E1-P4 (D10) — panel "Người phụ không tên" của MỘT tập, nằm trong cột phải của chặng ⑤ (dưới meta tập, trên bản đọc kịch bản) +
// thanh hàng loạt "Đề xuất người phụ cho các tập chưa có (N)" ở đầu vùng đọc. Chỉ VẼ: mọi quyết định bật/tắt nút, chữ, thẻ nằm ở
// `visualExtrasView.ts` (đã có test); ghi / chạy AI đi qua `useVisualExtras` do ScriptStage truyền xuống.
import React, { useEffect, useState } from 'react';
import { Input, Space, Switch, Tag, Tooltip, Typography } from 'antd';
import Button from './WbButton';
import type { VisualExtrasApi } from './useVisualExtras';
import {
  bulkBarView, editValidation, ExtrasCtx, ExtrasTone, itemView, panelState, previewBlockLine,
} from './visualExtrasView';

const { Text } = Typography;
const TONE_COLOR: Record<ExtrasTone, string | undefined> = { ai: 'blue', user: 'green', keep: 'purple', warn: 'orange', mut: undefined };
const tagStyle: React.CSSProperties = { fontSize: 11, lineHeight: '16px', margin: '0 4px 2px 0', padding: '0 6px' };
const boxStyle: React.CSSProperties = { border: '1px solid #d6e4ff', background: '#fafcff', borderRadius: 8, padding: 10, margin: '8px 0 12px' };

const ctxOf = (api: VisualExtrasApi, locked: boolean, shellBusy: boolean): ExtrasCtx => ({
  stageLocked: locked, working: api.working, jobRunning: api.jobRunning, shellBusy,
  loaded: api.summary !== null, loadFailed: api.loadFailed, runError: api.runError,
});

/** Nút có thể khoá: bọc `span` để Tooltip lý do vẫn hiện khi nút `disabled`. */
const guard = (why: string | undefined, node: React.ReactElement) => (why ? <Tooltip title={why}><span>{node}</span></Tooltip> : node);

// ─── Thanh hàng loạt ───────────────────────────────────────────────────────

interface BarProps { api: VisualExtrasApi; locked: boolean; shellBusy: boolean }

export const VisualExtrasBulkBar: React.FC<BarProps> = ({ api, locked, shellBusy }) => {
  const ctx = ctxOf(api, locked, shellBusy);
  const box: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: '#f0f5ff', border: '1px solid #d6e4ff', borderRadius: 6, padding: '8px 10px', marginBottom: 8,
  };
  if (!ctx.loaded) {
    return (
      <div style={box}>
        <b>Người phụ không tên:</b>
        {ctx.loadFailed
          ? <Space size={4}><Text type="secondary">Không tải được danh sách người phụ</Text><Button size="small" type="link" onClick={api.reload}>Thử lại</Button></Space>
          : <Text type="secondary">Đang tải…</Text>}
      </div>
    );
  }
  const bar = bulkBarView(api.summary, ctx, api.missing);
  return (
    <div style={box}>
      <b>Người phụ không tên · cả truyện:</b>
      <span>{bar.chips.map((c) => <Tag key={c.key} color={TONE_COLOR[c.tone]} style={{ marginInlineEnd: 4 }}>{c.label}</Tag>)}</span>
      {api.jobRunning && <Tag color="blue">{`Đang quét kịch bản…${api.progressText ? ` ${api.progressText}` : ''}`}</Tag>}
      {guard(bar.runReason, <Button size="small" type="primary" disabled={!bar.canRun} onClick={() => api.requestRun('missing')}>{bar.runLabel}</Button>)}
      <Text type="secondary" style={{ fontSize: 13 }}>{bar.hint}</Text>
    </div>
  );
};

// ─── Panel một tập ─────────────────────────────────────────────────────────

interface Props {
  episode: number;
  api: VisualExtrasApi;
  /** Chặng ⑤ đang khoá. */
  locked: boolean;
  /** Có việc nền khác / đang viết kịch bản (`busy` của EpisodeReader). */
  shellBusy: boolean;
}

/** Ô sửa / thêm đang mở. `id === null` ⇒ thêm mới. `conflict`: vừa dính 409 — chờ bản mới về rồi rebase `baseVersion`, GIỮ NGUYÊN bản nháp. */
interface Draft { id: string | null; label: string; text: string; baseVersion: string; conflict: boolean }

const VisualExtrasPanel: React.FC<Props> = ({ episode, api, locked, shellBusy }) => {
  const [draft, setDraft] = useState<Draft | null>(null);
  const ev = api.episodeView(episode);
  const ctx = ctxOf(api, locked, shellBusy);
  const panel = panelState(ev, ctx, api.limits);

  // Đổi tập (EpisodeReader remount theo `key`) hoặc mục đang sửa bị xoá ở nơi khác ⇒ bỏ ô sửa.
  const draftId = draft?.id ?? null;
  const itemStillThere = draftId === null || !!ev?.items.some((i) => i.id === draftId);
  useEffect(() => { if (draft && !itemStillThere && !draft.conflict) setDraft(null); }, [draft, itemStillThere]);
  // 409: bản mới đã về (version đổi) ⇒ rebase baseVersion, giữ bản nháp (P3 review N5).
  const curVersion = ev?.version;
  useEffect(() => {
    if (draft?.conflict && curVersion && curVersion !== draft.baseVersion) setDraft((d) => (d ? { ...d, baseVersion: curVersion, conflict: false } : d));
  }, [draft, curVersion]);

  const header = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
      <b>{panel.title}</b>
      <Space size={6} wrap>
        {panel.running && <Tag color="blue" style={tagStyle}>{`Đang quét kịch bản…${api.progressText ? ` ${api.progressText}` : ''}`}</Tag>}
        {panel.phase !== 'loading' && panel.phase !== 'error' && panel.phase !== 'noScript' && (
          <>
            {guard(panel.addReason, <Button size="small" disabled={!panel.canAdd || draft !== null} onClick={() => setDraft({ id: null, label: '', text: '', baseVersion: ev?.version ?? 'none', conflict: false })}>+ Thêm tay</Button>)}
            {guard(panel.reasonDisabled, <Button size="small" type={panel.phase === 'empty' ? 'primary' : 'default'} disabled={!panel.canScan} onClick={() => api.requestRun('one', episode)}>{panel.scanLabel}</Button>)}
          </>
        )}
      </Space>
    </div>
  );

  if (panel.phase === 'loading') return <div style={boxStyle}>{header}<Text type="secondary">…</Text></div>;
  if (panel.phase === 'error') {
    return (
      <div style={boxStyle}>
        {header}
        <Space size={4}><Text type="secondary">{panel.message}</Text><Button size="small" type="link" onClick={api.reload}>Thử lại</Button></Space>
      </div>
    );
  }
  if (panel.phase === 'noScript') {
    return <div style={boxStyle}>{header}<Text type="secondary">{panel.message}</Text></div>;
  }

  const form = (d: Draft) => {
    const v = editValidation(d.label, d.text, api.limits);
    const preview = previewBlockLine(d.label, d.text);
    const labelBad = v.code === 'EMPTY_LABEL' || v.code === 'LABEL_INVALID' || v.code === 'LABEL_TOO_LONG';
    const textBad = v.code === 'TOO_LONG';
    const save = async () => {
      const out = d.id === null
        ? await api.add(episode, v.normalizedLabel, v.normalizedText, d.baseVersion)
        : await api.edit(episode, d.id, v.normalizedLabel, v.normalizedText, d.baseVersion);
      if (out.closeForm) setDraft(null);
      else if (out.conflict) setDraft((cur) => (cur ? { ...cur, conflict: true } : cur));
    };
    return (
      <div style={{ border: '1px solid #1677ff', borderRadius: 6, padding: 8, margin: '6px 0', background: '#fff' }}>
        <Text strong style={{ fontSize: 12 }}>{d.id === null ? 'Thêm người phụ' : 'Đang sửa'}</Text>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
          <div style={{ flex: '1 1 160px' }}>
            <Text type="secondary" style={{ fontSize: 11 }}>{`Nhãn (≤${v.labelMax}, không dấu hai chấm)`}</Text>
            <Input
              size="small" value={d.label} status={labelBad && d.label.trim() !== '' ? 'error' : undefined} autoFocus={d.id === null}
              onChange={(e) => setDraft((cur) => (cur ? { ...cur, label: e.target.value } : cur))}
            />
            <Text type={labelBad && d.label.trim() !== '' ? 'danger' : 'secondary'} style={{ fontSize: 11 }}>{`${v.labelCount}/${v.labelMax}`}</Text>
          </div>
          <div style={{ flex: '3 1 260px' }}>
            <Text type="secondary" style={{ fontSize: 11 }}>Mô tả một dòng (tiếng Anh)</Text>
            <Input.TextArea
              rows={2} value={d.text} status={textBad ? 'error' : undefined}
              onChange={(e) => setDraft((cur) => (cur ? { ...cur, text: e.target.value } : cur))}
            />
            <Text type={textBad ? 'danger' : 'secondary'} style={{ fontSize: 11 }}>
              {`${v.textCount}${v.textMax !== null ? `/${v.textMax}` : ''} · không chứa tên gốc nhân vật (nếu có sẽ báo lỗi)`}
            </Text>
          </div>
        </div>
        {preview && <div><Text type="secondary" style={{ fontSize: 11 }}>Khối sẽ in: <b>{preview}</b></Text></div>}
        <Space size={6} style={{ marginTop: 4 }} wrap>
          {guard(panel.reasonDisabled, <Button size="small" type="primary" disabled={!v.ok || ctx.working || !!panel.reasonDisabled} onClick={save}>Lưu (thành “Đã sửa tay” + Giữ)</Button>)}
          <Button size="small" onClick={() => setDraft(null)}>Huỷ</Button>
          {v.message && v.code && (d.label.trim() !== '' || d.text.trim() !== '') && <Text type="secondary" style={{ fontSize: 11 }}>{v.message}</Text>}
          {d.conflict && <Text type="warning" style={{ fontSize: 11 }}>Đang tải bản mới…</Text>}
        </Space>
      </div>
    );
  };

  return (
    <div style={boxStyle} role="region" aria-label={`Người phụ không tên của tập ${episode}`}>
      {header}
      {panel.errorLine && (
        <div style={{ marginBottom: 6 }}>
          <Tag color="red" style={tagStyle}>{panel.errorLine}</Tag>
          {guard(panel.reasonDisabled, <Button size="small" type="link" disabled={!panel.canScan} onClick={() => api.requestRun('one', episode)}>Thử lại</Button>)}
        </div>
      )}
      {panel.banner && <div style={{ marginBottom: 6 }}><Text type="warning" style={{ fontSize: 13 }}>{panel.banner}</Text></div>}
      {panel.message && (
        <div style={{ border: '1px dashed #98a2b3', borderRadius: 8, padding: 8, background: '#fcfcfd' }}>
          <Text>{panel.message}</Text>{' '}
        </div>
      )}
      {draft && draft.id === null && form(draft)}
      {(ev?.items ?? []).map((it) => {
        const v = itemView(it, panel);
        if (draft && draft.id === it.id) return <React.Fragment key={it.id}>{form(draft)}</React.Fragment>;
        return (
          <div key={it.id} style={{ borderTop: '1px solid #eaecf0', padding: '8px 0', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <b>{v.label}</b>{' '}
              {v.tags.map((t) => <Tag key={t.key} color={TONE_COLOR[t.tone]} style={tagStyle}>{t.label}</Tag>)}
              <div style={{ margin: '2px 0' }}>{v.text}</div>
              {v.evidence && <div><Text type="secondary" style={{ fontSize: 11 }}>Trích: <i>{v.evidence}</i></Text></div>}
              {v.blockLine && <div><Text type="secondary" style={{ fontSize: 11 }}>Khối sẽ in: <b>{v.blockLine}</b></Text></div>}
              {v.note && <div><Text type="secondary" style={{ fontSize: 11 }}>{v.note}</Text></div>}
            </div>
            <Space size={6} wrap style={{ flex: '0 0 auto' }}>
              {guard(panel.reasonDisabled, (
                <Button size="small" disabled={!v.canEdit || draft !== null} onClick={() => setDraft({ id: it.id, label: it.label, text: it.text, baseVersion: ev?.version ?? 'none', conflict: false })}>Sửa</Button>
              ))}
              {guard(panel.reasonDisabled, (
                <Space size={4}>
                  <Text type="secondary" style={{ fontSize: 11 }}>Giữ</Text>
                  <Switch size="small" checked={it.locked} disabled={!v.canLock} onChange={(l) => { void api.setLock(episode, it.id, l, ev?.version ?? 'none'); }} />
                </Space>
              ))}
              {guard(v.removeReason, <Button size="small" danger disabled={!v.canRemove} onClick={() => api.requestRemove(episode, it.id, it.label, ev?.version ?? 'none')}>Xoá</Button>)}
            </Space>
          </div>
        );
      })}
      {panel.count > 0 && <div style={{ marginTop: 6 }}><Text type="warning" style={{ fontSize: 13 }}>{api.hintFor(episode)}</Text></div>}
    </div>
  );
};

export default VisualExtrasPanel;
